import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export type ToastKind = 'success' | 'error' | 'info';

export interface IToast {
  id: number;
  kind: ToastKind;
  text: string;
}

interface IToastContext {
  show: (input: { kind?: ToastKind; text: string; timeoutMs?: number }) => void;
}

const Ctx = createContext<IToastContext | null>(null);

/**
 * Lightweight, dependency-free toast system. Provider mounts a stacked
 * list of transient banners in the bottom-right of the viewport; each
 * auto-dismisses after `timeoutMs` (default 4s).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<IToast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<IToastContext['show']>(
    ({ kind = 'info', text, timeoutMs = 4_000 }) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, kind, text }]);
      window.setTimeout(() => dismiss(id), timeoutMs);
    },
    [dismiss],
  );

  return (
    <Ctx.Provider value={{ show }}>
      {children}
      <div style={stackStyle} aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): IToastContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

function ToastItem({ toast, onDismiss }: { toast: IToast; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handle = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(handle);
  }, []);
  return (
    <div
      role={toast.kind === 'error' ? 'alert' : 'status'}
      style={{
        ...itemStyle,
        ...kindStyles[toast.kind],
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
      }}
      onClick={onDismiss}
    >
      <span style={{ flex: 1 }}>{toast.text}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        aria-label="Dismiss"
        style={dismissBtnStyle}
      >
        ×
      </button>
    </div>
  );
}

const stackStyle: CSSProperties = {
  position: 'fixed',
  bottom: 'var(--sp-4)',
  right: 'var(--sp-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  zIndex: 1000,
  pointerEvents: 'none',
};

const itemStyle: CSSProperties = {
  pointerEvents: 'auto',
  minWidth: 260,
  maxWidth: 420,
  padding: 'var(--sp-2) var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  fontSize: 'var(--fs-sm)',
  boxShadow: 'var(--surface-elevated)',
  cursor: 'pointer',
  transition: 'opacity 120ms ease, transform 120ms ease',
};

const kindStyles: Record<ToastKind, CSSProperties> = {
  success: {
    background: 'var(--status-success-bg, #0c2c19)',
    color: 'var(--status-success, #6bd599)',
    border: '1px solid var(--status-success, #2b6b40)',
  },
  error: {
    background: 'var(--status-danger-bg, #2c0c0c)',
    color: 'var(--status-danger, #ff7676)',
    border: '1px solid var(--status-danger, #6b2b2b)',
  },
  info: {
    background: 'var(--bg-chrome, #1a1a1f)',
    color: 'var(--text-primary, #e6e6e6)',
    border: '1px solid var(--border-subtle, #2a2a30)',
  },
};

const dismissBtnStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: 'var(--fs-lg)',
  lineHeight: 1,
  padding: '0 var(--sp-1)',
};
